import React, { useEffect, useRef, useState } from 'react';
import { Button, Card, Form, FormInstance, InputNumber, message, Radio, Select, Tag } from 'antd';
import useDebounce from '@app/hooks/useDebounce';
import { getProductForOrdersApi } from '@app/api/product.api';
import { ProductModel } from '@app/interfaces/product';
import { formatNumberInInput, formatMoney } from '@app/utils/utils';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import ProductionItemList from './ProductItemList';
import { useAppDispatch, useAppSelector } from '@app/hooks/reduxHooks';
import { RootState } from '@app/store/store';
import { addProductItem, deleteProductItem, resetProductItems, updateProductItem } from '@app/store/slices/orderSlice';
import 'components/order/Order.styles.css';
import { MdOutlineGridView, MdOutlineInventory2, MdOutlinePayments, MdOutlineImage } from 'react-icons/md';
import { v4 as uuidv4 } from 'uuid';
import ProductTableRow from './ProductTableRow';
import CustomerPaymentSection from './CustomerPaymentSection';
import PointsDiscountRow from './PointsDiscountRow';

interface OrderProductItemsProps {
  form: FormInstance;
  onCalcTotal: () => void;
  returnOrder?: boolean;
}

// ── Shared class strings (migrated from Order.styles.css) ───────────────────

const SUMMARY_ROW_CLS = 'tw-flex tw-min-h-10 tw-flex-wrap tw-items-center tw-justify-between tw-gap-y-1 tw-py-0.5';
const SUMMARY_LABEL_CLS = 'tw-text-[13px] tw-text-on-surface-variant';

// ── Toggle row helpers ──────────────────────────────────────────────────────

interface ToggleRowProps {
  label: string;
  shortcut?: string;
  name: string;
  onCalcTotal: () => void;
  isPercent?: boolean;
}

const ToggleSummaryRow: React.FC<ToggleRowProps> = ({ label, shortcut, name, onCalcTotal, isPercent }) => {
  const [open, setOpen] = useState(false);
  // Đơn đã có giá trị (mở đơn cũ để sửa) thì hiển thị input luôn — hàng collapsed
  // "---" từng che mất chiết khấu đang áp dụng, khiến tổng bị tính lại thiếu chiết khấu.
  const fieldValue = Form.useWatch(name);
  useEffect(() => {
    if (Number(fieldValue) > 0) setOpen(true);
  }, [fieldValue]);

  return (
    <div>
      {!open ? (
        <div className={`${SUMMARY_ROW_CLS} -tw-mx-1 tw-cursor-pointer tw-rounded-lg tw-px-1 tw-transition-colors tw-duration-200 hover:tw-bg-surface-subtle`} onClick={() => setOpen(true)}>
          <span className="tw-text-[13px] tw-text-primary tw-underline tw-decoration-dotted">
            {label}
            {shortcut && <span className="tw-ml-1 tw-text-[11px] tw-font-normal tw-text-outline">({shortcut})</span>}
          </span>
          <span className="tw-text-[12px] tw-text-outline">---</span>
        </div>
      ) : (
        <div className={SUMMARY_ROW_CLS}>
          <span className={SUMMARY_LABEL_CLS}>{label}</span>
          <Form.Item name={name} style={{ marginBottom: 0 }}>
            <InputNumber
              className="order-input-item text-primary"
              defaultValue={0}
              min={0}
              max={isPercent ? 100 : undefined}
              formatter={(v) => (isPercent ? `${v}%` : formatNumberInInput(v))}
              onChange={onCalcTotal}
              inputMode="numeric"
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>
      )}
    </div>
  );
};

// ── Product search option renderer ─────────────────────────────────────────

const optionItem = (item: ProductModel) => (
  <div
    className="tw-border-0 tw-border-b tw-border-solid tw-border-surface-container-low"
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      padding: '12px 8px',
      opacity: item.onhand <= 0 && item.product_type === 'goods' ? 0.5 : 1,
      minHeight: '70px',
    }}
  >
    <div
      style={{
        width: 50,
        height: 50,
        borderRadius: 8,
        overflow: 'hidden',
        flexShrink: 0,
        background: item.onhand <= 0 ? '#fff1f0' : '#f5f5f5',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        border: item.onhand <= 0 ? '2px solid #ffccc7' : 'none',
        position: 'relative',
      }}
    >
      {item?.image_url ? (
        <img
          src={item.image_url}
          alt={item.product_code}
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            filter: item.onhand <= 0 ? 'grayscale(100%)' : 'none',
          }}
        />
      ) : (
        <MdOutlineImage style={{ fontSize: '24px', opacity: 0.3 }} />
      )}
      {item.onhand <= 0 && item.product_type === 'goods' && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(255, 77, 79, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <span className="tw-text-[10px] tw-font-semibold tw-text-error">HẾT</span>
        </div>
      )}
    </div>
    <div style={{ flex: 1, minWidth: 0 }}>
      <div
        className="tw-text-on-surface"
        style={{
          fontWeight: 600,
          fontSize: '14px',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          marginBottom: 6,
        }}
      >
        {item.name}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: '12px' }}>
        <Tag color="blue" style={{ margin: 0, fontSize: '11px', padding: '2px 8px' }}>
          {item.product_code}
        </Tag>
        <span className="tw-font-semibold tw-text-primary">{formatMoney(item.selling_price)}</span>
        <span className="tw-text-on-surface-variant">•</span>
        <span className={`tw-font-semibold ${item.onhand > 0 ? 'tw-text-success-teal' : 'tw-text-error'}`}>
          Tồn: {item.onhand} {item.unit}
        </span>
        <Tag
          color={item.product_type === 'goods' ? 'green' : 'orange'}
          style={{ margin: 0, fontSize: '11px', padding: '2px 8px' }}
        >
          {item.product_type}
        </Tag>
      </div>
    </div>
  </div>
);

// ── Main component ─────────────────────────────────────────────────────────

const OrderProductItems: React.FC<OrderProductItemsProps> = ({ form, onCalcTotal, returnOrder }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [products, setProducts] = useState<ProductModel[]>([]);
  const [showFormProductList, setShowFormProductList] = useState(false);
  const [selectedProductValue, setSelectedProductValue] = useState<string | undefined>(undefined);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  // Flag to prevent onDropdownVisibleChange from closing after a selection
  const keepOpenRef = useRef(false);

  const { productItems = [] } = useAppSelector((state: RootState) => state.order || {});
  const dispatch = useAppDispatch();
  const debouncedSearchTerm = useDebounce(searchTerm, 100);

  useEffect(() => {
    fetchProducts();
  }, [debouncedSearchTerm]);

  useEffect(() => {
    dispatch(resetProductItems([]));
  }, []);

  // On mount: if deferred (the default), make sure debit_amount = -total_amount so the
  // value is recorded correctly even before the user touches the radio
  useEffect(() => {
    const status = form.getFieldValue('payment_status');
    if (!status || status === 'deferred') {
      form.setFieldsValue({ payment_status: 'deferred' });
      const total = Number(form.getFieldValue('total_amount')) || 0;
      form.setFieldsValue({ customer_payment_amount: 0, debit_amount: -total });
    }
  }, []);

  useEffect(() => {
    form.setFieldsValue({ items: productItems });
  }, [productItems, form]);

  const fetchProducts = async () => {
    try {
      const response = await getProductForOrdersApi({ q: debouncedSearchTerm, page: 1, per_page: 20 });
      setProducts(response?.rows.map((item) => item.attributes) || []);
    } catch (error: any) {
      message.error(error.message);
    }
  };

  const handleUpdateField = (id: string, field: string, value: string | number) => {
    if (!productItems || productItems.length === 0) return;
    const updatedItem = productItems.find((item) => item.id === id);
    if (!updatedItem) return;

    if (field === 'qty') {
      if (isNaN(Number(value)) || Number(value) < 0) {
        message.error('Số lượng không hợp lệ');
        return;
      }
      if (Number(value) > updatedItem.onhand && updatedItem?.product_type === 'goods') {
        message.error(`Không đủ hàng trong kho (tồn kho: ${updatedItem.onhand})`);
        return;
      }
    }
    if (field === 'price' && (isNaN(Number(value)) || Number(value) < 0)) {
      message.error('Giá không hợp lệ');
      return;
    }

    dispatch(
      updateProductItem({
        ...updatedItem,
        [field]: value,
        amount:
          field === 'qty' || field === 'price'
            ? (field === 'qty' ? Number(value) : updatedItem.qty) *
              (field === 'price' ? Number(value) : updatedItem.price)
            : updatedItem.amount,
      }),
    );
  };

  const handleDeleteItem = (id: string) => dispatch(deleteProductItem(id));

  const onChangeProduct = (value: string) => {
    const data = products.find((item) => item.id === value);
    if (data) {
      dispatch(
        addProductItem({
          id: uuidv4(),
          name: data.name,
          unit: data.unit,
          qty: 1,
          price: data.selling_price,
          selling_price: data.selling_price,
          amount: data.selling_price,
          product_code: data.product_code,
          product_type: data.product_type,
          onhand: data.onhand,
          remark: '',
        }),
      );
      // Keep dropdown open for next pick
      setSelectedProductValue(undefined);
      setSearchTerm('');
      keepOpenRef.current = true;
      setTimeout(() => {
        setDropdownOpen(true);
        keepOpenRef.current = false;
      }, 0);
    } else {
      message.error(`Item ${value} đang tồn tại trong danh sách đơn hàng.`);
    }
  };

  const productOptions = products.map((item) => ({
    value: item.id,
    label: optionItem(item),
    name: `${item.product_code}-${item.name}`,
  }));

  const totalQty = productItems.reduce((s, p) => s + p.qty, 0);

  return (
    <div>
      {/* ── Products card ── */}
      <Card style={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: 12 }}>
        <div
          className="order-section-title"
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <MdOutlineInventory2 style={{ fontSize: 20, color: 'var(--primary-color)' }} />
            Sản phẩm đơn hàng
          </span>
          {productItems.length > 0 && (
            <Tag color="var(--primary-color)" style={{ fontSize: '12px', padding: '4px 12px', borderRadius: 12, fontWeight: 600 }}>
              {productItems.length} sản phẩm
            </Tag>
          )}
        </div>

        {/* Search bar */}
        <Form.Item name="items">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
            <Select
              allowClear
              showSearch
              open={dropdownOpen}
              onDropdownVisibleChange={(visible) => {
                if (keepOpenRef.current) return;
                setDropdownOpen(visible);
              }}
              placeholder="Tìm kiếm theo tên, mã sản phẩm..."
              optionFilterProp="children"
              value={selectedProductValue}
              onChange={onChangeProduct}
              onSearch={(v) => setSearchTerm(v)}
              filterOption={(input, option) => (option?.name ?? '').toLowerCase().includes(input.toLowerCase())}
              options={productOptions}
              optionLabelProp="name"
              suffixIcon={<SearchOutlined className="tw-text-outline" />}
              style={{ flex: 1 }}
              size="large"
              notFoundContent={
                <div className="tw-text-on-surface-variant" style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <MdOutlineInventory2 style={{ fontSize: 32, marginBottom: 8, opacity: 0.3 }} />
                  <div style={{ fontSize: 14, fontWeight: 500 }}>Không tìm thấy sản phẩm</div>
                </div>
              }
            />
            <Button
              type="primary"
              ghost
              size="large"
              onClick={() => setShowFormProductList(true)}
              icon={<MdOutlineGridView style={{ fontSize: 18 }} />}
              style={{ borderRadius: 8, fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, minWidth: 110 }}
            >
              Danh sách
            </Button>
          </div>
        </Form.Item>

        {/* Empty state */}
        {productItems.length === 0 && (
          <div className="tw-rounded-lg tw-border-2 tw-border-dashed tw-border-outline-variant tw-bg-surface-subtle tw-px-5 tw-py-10 tw-text-center tw-text-on-surface-variant">
            <MdOutlineInventory2 style={{ fontSize: 40, marginBottom: 8, opacity: 0.35 }} />
            <p style={{ fontSize: 16, marginBottom: 8 }}>Chưa có sản phẩm nào</p>
            <p style={{ fontSize: 14 }}>Vui lòng chọn sản phẩm để thêm vào đơn hàng</p>
          </div>
        )}

        {/* Table header + rows: grid có tổng track cố định ~440px, còn
            .order-container đặt overflow-x hidden nên trên màn hẹp phải tự
            tạo vùng cuộn ngang — nếu không cột Thành tiền/nút xoá bị cắt mất. */}
        {productItems.length > 0 && (
          <div className="tw-overflow-x-auto">
            <div className="tw-min-w-[440px]">
              <div className="tw-grid tw-grid-cols-[40px_1fr_90px_130px_90px_28px] tw-gap-2 tw-border-0 tw-border-b-2 tw-border-solid tw-border-surface-container-low tw-pb-2 tw-pt-1.5 tw-text-[11px] tw-font-bold tw-uppercase tw-tracking-[0.4px] tw-text-on-surface-variant">
                <span style={{ gridColumn: '1/3' }}>
                  Sản phẩm <span className="tw-text-error">*</span>
                </span>
                <span style={{ textAlign: 'center' }}>
                  Số lượng <span className="tw-text-error">*</span>
                </span>
                <span>
                  Đơn giá <span className="tw-text-error">*</span>
                </span>
                <span style={{ textAlign: 'right' }}>Thành tiền</span>
                <span />
              </div>

              {productItems.map((item, index) => (
                <ProductTableRow
                  key={item.id}
                  item={item}
                  index={index}
                  onUpdateField={handleUpdateField}
                  onDelete={handleDeleteItem}
                />
              ))}
            </div>
          </div>
        )}

        {/* Add more link */}
        {productItems.length > 0 && (
          <div style={{ paddingTop: 8 }}>
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={() => setShowFormProductList(true)}
              style={{ paddingLeft: 0, fontSize: 13 }}
            >
              Thêm sản phẩm hoặc dịch vụ tùy chỉnh
            </Button>
          </div>
        )}
      </Card>

      {/* ── Payment summary ── */}
      <div className="tw-rounded-[10px] tw-border tw-border-solid tw-border-surface-container-low tw-bg-surface-container-lowest tw-p-4 tw-shadow-[0_2px_8px_rgba(0,0,0,0.06)]" style={{ marginBottom: 12 }}>
        <div
          className="order-section-title"
          style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          <MdOutlinePayments style={{ fontSize: 20, color: 'var(--primary-color)' }} />
          Thanh toán
        </div>

        {/* Tổng tiền hàng */}
        <div className={SUMMARY_ROW_CLS}>
          <span className={SUMMARY_LABEL_CLS}>
            Tổng tiền hàng
            {totalQty > 0 && (
              <Tag color="processing" style={{ marginLeft: 6 }}>
                {totalQty} SP
              </Tag>
            )}
          </span>
          <Form.Item name="before_discount_amount" style={{ marginBottom: 0 }}>
            <InputNumber
              className="order-input-item"
              formatter={(v) => formatNumberInInput(Number(v))}
              readOnly
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>

        {/* Discount toggle */}
        <ToggleSummaryRow
          label="Thêm giảm giá"
          shortcut="F6"
          name="discount_percent"
          onCalcTotal={onCalcTotal}
          isPercent
        />

        {/* Hidden discount_amount field */}
        <Form.Item name="discount_amount" hidden>
          <InputNumber />
        </Form.Item>

        {/* Shipping toggle */}
        <ToggleSummaryRow label="Thêm phí giao hàng" shortcut="F7" name="shipping_fee" onCalcTotal={onCalcTotal} />

        {/* VAT */}
        <div className={SUMMARY_ROW_CLS}>
          <span className={SUMMARY_LABEL_CLS}>VAT (%)</span>
          <Form.Item name="vat_percent" style={{ marginBottom: 0 }}>
            <InputNumber
              className="order-input-item text-primary"
              defaultValue={0}
              min={0}
              max={100}
              formatter={(v) => `${v}%`}
              onChange={onCalcTotal}
              inputMode="numeric"
              style={{ width: 160 }}
            />
          </Form.Item>
          <Form.Item name="vat_amount" hidden>
            <InputNumber />
          </Form.Item>
        </div>

        {/* Other amount */}
        <div className={SUMMARY_ROW_CLS}>
          <span className={SUMMARY_LABEL_CLS}>Chi phí khác</span>
          <Form.Item name="other_amount" style={{ marginBottom: 0 }}>
            <InputNumber
              defaultValue={0}
              className="order-input-item text-primary"
              formatter={(v) => formatNumberInInput(v)}
              onChange={onCalcTotal}
              inputMode="numeric"
              style={{ width: 160 }}
            />
          </Form.Item>
        </div>

        {!returnOrder && (
          <PointsDiscountRow
            rowClassName={SUMMARY_ROW_CLS}
            labelClassName={SUMMARY_LABEL_CLS}
            onCalcTotal={onCalcTotal}
          />
        )}

        {/* Thành tiền */}
        <div className={`${SUMMARY_ROW_CLS} tw-mt-1 tw-border-0 tw-border-t-2 tw-border-solid tw-border-surface-container-low tw-pt-2.5`}>
          <strong className="tw-text-[15px] tw-text-on-surface">{returnOrder ? 'Tổng trả khách' : 'Thành tiền'}</strong>
          <Form.Item name="total_amount" style={{ marginBottom: 0 }}>
            <InputNumber
              formatter={(v) => formatNumberInInput(Number(v || 0))}
              className="order-input-item"
              readOnly
              style={{ fontWeight: 700, fontSize: 16, width: 160 }}
            />
          </Form.Item>
        </div>

        {/* Hidden submit fields */}
        <Form.Item name="before_discount_amount" hidden>
          <InputNumber />
        </Form.Item>

        {/* Payment status radio */}
        {!returnOrder && (
          <div style={{ marginTop: 12 }}>
            <Form.Item name="payment_status" style={{ marginBottom: 0 }}>
              <Radio.Group
                onChange={(e) => {
                  const status = e.target.value;
                  const total = Number(form.getFieldValue('total_amount')) || 0;
                  if (status === 'deferred') {
                    // Customer pays nothing now → full amount becomes debt
                    form.setFieldsValue({ customer_payment_amount: 0, debit_amount: -total });
                  } else {
                    // Switching to paid → recalculate from whatever customer_payment_amount is set
                    const paid = Number(form.getFieldValue('customer_payment_amount')) || 0;
                    const owed = paid > total ? 0 : total - paid;
                    form.setFieldsValue({ debit_amount: -owed });
                  }
                }}
              >
                <Radio value="paid">Đã thanh toán</Radio>
                <Radio value="deferred" defaultChecked>
                  Thanh toán sau
                </Radio>
              </Radio.Group>
            </Form.Item>
          </div>
        )}

        {/* Chi tiết thanh toán — phương thức (TM/CK) hiển thị chung cho cả hai
            trạng thái; phần "Khách thanh toán"/"Công nợ" tự đổi theo payment_status. */}
        {!returnOrder && <CustomerPaymentSection form={form} />}

        {/* Always keep debit_amount in the form, hidden */}
        <Form.Item name="debit_amount" hidden>
          <InputNumber />
        </Form.Item>
      </div>

      {showFormProductList && (
        <ProductionItemList open={showFormProductList} setOpen={setShowFormProductList} title="Chọn sản phẩm" />
      )}
    </div>
  );
};

export default OrderProductItems;
